<template>
  <div class="user-detail" v-loading="loading">
    <template v-if="detail.base_info && detail.base_info.id">
      <div class="summary-card">
        <div class="summary-main">
          <img
            :src="getAvatar(detail.base_info.avatar)"
            alt=""
            class="avatar"
          />
          <div class="summary-text">
            <div class="title-row">
              <strong>{{
                detail.base_info.nickname || detail.base_info.username || "--"
              }}</strong>
              <el-tag size="mini" effect="plain">
                ID {{ detail.base_info.id || "--" }}
              </el-tag>
            </div>
            <p>账号：{{ detail.base_info.username || "--" }}</p>
            <p>APP：{{ detail.base_info.app_name || "--" }}</p>
            <p>渠道：{{ detail.base_info.channel_name || "--" }}</p>
          </div>
        </div>

        <div class="summary-side">
          <div>今日收益：{{ detail.ad_stats.today_total_ecpm_display || "--" }}</div>
          <div>
            风险状态：
            <el-tag size="mini" :type="riskTagType(detail.risk_stats.risk_level)">
              {{ riskLevelText(detail.risk_stats.risk_level) }}
            </el-tag>
          </div>
          <div>最近登录：{{ formatTime(detail.base_info.last_login_time) }}</div>
        </div>
      </div>

      <div class="detail-grid">
        <el-card shadow="never" class="detail-card">
          <div slot="header" class="card-title">基础信息</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="用户ID">
              {{ detail.base_info.id || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="用户名">
              {{ detail.base_info.username || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="昵称">
              {{ detail.base_info.nickname || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="APP">
              {{ detail.base_info.app_name || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="渠道">
              {{ detail.base_info.channel_name || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="注册时间">
              {{ formatTime(detail.base_info.created_time) }}
            </el-descriptions-item>
            <el-descriptions-item label="最后登录">
              {{ formatTime(detail.base_info.last_login_time) }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="detail-card">
          <div slot="header" class="card-title">账户信息</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="Open ID">
              {{ detail.account_info.open_id || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="Session ID">
              {{ detail.account_info.session_id || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="封禁到期">
              {{ formatTime(detail.account_info.banned_time) }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="detail-card">
          <div slot="header" class="card-title">设备信息</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="手机型号">
              {{ detail.device_info.phone_model || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="手机品牌">
              {{ detail.device_info.phone_manufacturer || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="系统版本">
              {{ detail.device_info.phone_version || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="SDK版本">
              {{ detail.device_info.sdk_version || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="屏幕分辨率">
              {{
                `${detail.device_info.phone_width || "--"} x ${
                  detail.device_info.phone_height || "--"
                }`
              }}
            </el-descriptions-item>
            <el-descriptions-item label="网络">
              {{ detail.device_info.phone_network_name || "--" }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="detail-card">
          <div slot="header" class="card-title">地理信息</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="最新IP">
              {{ detail.geo_info.ip || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="省市区" :span="2">
              {{
                [
                  detail.geo_info.province,
                  detail.geo_info.city,
                  detail.geo_info.area,
                ]
                  .filter(Boolean)
                  .join(" / ") || "--"
              }}
            </el-descriptions-item>
            <el-descriptions-item label="街道">
              {{ detail.geo_info.street || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="地址">
              {{ detail.geo_info.address || "--" }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="detail-card">
          <div slot="header" class="card-title">广告统计</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="今日激励广告数">
              {{ detail.ad_stats.today_reward_ad_count || 0 }}
            </el-descriptions-item>
            <el-descriptions-item label="今日其他广告数">
              {{ detail.ad_stats.today_other_ad_count || 0 }}
            </el-descriptions-item>
            <el-descriptions-item label="今日总广告数">
              {{ detail.ad_stats.today_total_ad_count || 0 }}
            </el-descriptions-item>
            <el-descriptions-item label="今日总收益">
              {{ detail.ad_stats.today_total_ecpm_display || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="昨日收益">
              {{ detail.ad_stats.yesterday_total_ecpm_display || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="累计余额">
              {{ detail.ad_stats.total_balance || "--" }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="detail-card">
          <div slot="header" class="card-title">风险提示</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="是否风险用户">
              <el-tag :type="detail.risk_stats.is_risk_user ? 'danger' : 'success'">
                {{ detail.risk_stats.is_risk_user ? "是" : "否" }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="风险等级">
              <el-tag :type="riskTagType(detail.risk_stats.risk_level)">
                {{ riskLevelText(detail.risk_stats.risk_level) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="查看方式" :span="2">
              详细风险信息请回到用户列表点击“风险详情”查看。
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="detail-card">
          <div slot="header" class="card-title">最近登录摘要</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="登录时间">
              {{ formatTime(detail.latest_login.created_time) }}
            </el-descriptions-item>
            <el-descriptions-item label="IP">
              {{ detail.latest_login.ip || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="设备ID">
              {{ detail.latest_login.phone_id || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="会话ID">
              {{ detail.latest_login.session_id || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="渠道ID">
              {{ detail.latest_login.channel_id || "--" }}
            </el-descriptions-item>
            <el-descriptions-item label="网络">
              {{ detail.latest_login.phone_network_name || "--" }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>
      </div>

      <el-card shadow="never" class="detail-card recent-rules-card">
        <div slot="header" class="card-title">最近规则命中</div>
        <el-table :data="detail.recent_rules || []" border stripe>
          <el-table-column prop="id" label="记录ID" width="90" align="center" />
          <el-table-column prop="rule_name" label="规则名称" min-width="180" />
          <el-table-column prop="rule_key" label="规则Key" min-width="140" />
          <el-table-column label="规则类型" width="100" align="center">
            <template slot-scope="{ row }">
              {{ row.rule_type == 1 ? "登录" : row.rule_type == 2 ? "拉广" : "--" }}
            </template>
          </el-table-column>
          <el-table-column label="处罚类型" width="100" align="center">
            <template slot-scope="{ row }">
              {{ row.type == 1 ? "预警" : "封禁" }}
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="180" />
          <el-table-column label="命中时间" min-width="160" align="center">
            <template slot-scope="{ row }">
              {{ formatTime(row.created_time) }}
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>
  </div>
</template>

<script>
export default {
  props: {
    editId: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      detail: {
        base_info: {},
        account_info: {},
        device_info: {},
        geo_info: {},
        ad_stats: {},
        risk_stats: {},
        latest_login: {},
        recent_rules: [],
      },
    };
  },
  methods: {
    async getDetails() {
      if (!this.editId) {
        return;
      }

      this.loading = true;
      try {
        const data = await this.$api.getUser.getUserInfo({ id: this.editId });
        this.detail = {
          base_info: data.data.base_info || {},
          account_info: data.data.account_info || {},
          device_info: data.data.device_info || {},
          geo_info: data.data.geo_info || {},
          ad_stats: data.data.ad_stats || {},
          risk_stats: data.data.risk_stats || {},
          latest_login: data.data.latest_login || {},
          recent_rules: data.data.recent_rules || [],
        };
      } finally {
        this.loading = false;
      }
    },
    formatTime(value) {
      return value ? this.$fnc.getTimeFormat(value, "ymdhms") : "--";
    },
    riskTagType(level) {
      if (level === "high") return "danger";
      if (level === "medium") return "warning";
      if (level === "low") return "primary";
      return "success";
    },
    riskLevelText(level) {
      if (level === "high") return "高风险";
      if (level === "medium") return "中风险";
      if (level === "low") return "低风险";
      return "安全";
    },
    getAvatar(avatar) {
      if (!avatar) {
        return "";
      }
      if (avatar.indexOf("http") >= 0 || avatar.indexOf("api") >= 0) {
        return avatar;
      }
      return `/api/${avatar}`;
    },
  },
  watch: {
    editId: {
      immediate: true,
      handler() {
        this.getDetails();
      },
    },
  },
};
</script>

<style lang="less" scoped>
.summary-card {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 18px;
  margin-bottom: 18px;
  border-radius: 16px;
  background: #f7faff;
  border: 1px solid #e5edf8;
}

.summary-main {
  display: flex;
  align-items: center;
}

.avatar {
  width: 64px;
  height: 64px;
  margin-right: 16px;
  border-radius: 18px;
  object-fit: cover;
}

.summary-text {
  p {
    margin: 8px 0 0;
    color: #667a9f;
  }
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.summary-side {
  min-width: 220px;
  padding: 10px 14px;
  border-radius: 14px;
  background: #fff;
  color: #516b96;

  div + div {
    margin-top: 10px;
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.detail-card {
  border-radius: 16px;
}

.card-title {
  font-weight: 600;
  color: #203864;
}

.recent-rules-card {
  margin-top: 16px;
}
</style>
